/**
 * 個人スマホ用アプリを「ホーム画面に追加」する案内（ブラウザで開いているときだけ出す）。
 *
 * QRで開いた船員が「何かが開いただけで、どうすればいいか分からない」とならないよう、
 * iPhone は Safari の共有→ホーム画面に追加 の手順、Android はボタン1つでインストールを出す。
 * 船内端末のAPK・ホーム画面から起動済みのときは出さない。
 */
import React, { useEffect, useState } from 'react';
import { isPersonalDevice } from '../services/api';
import { canPromptInstall, isIOS, isNativeShell, isStandalone, onInstallPromptChange, promptInstall } from '../utils/installPrompt';

const DISMISS_KEY = 'catms_install_guide_dismissed_at';
const DISMISS_DAYS = 7;

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return at > 0 && Date.now() - at < DISMISS_DAYS * 86400000;
  } catch { return false; }
}

const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-3px' }}>
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

export const InstallAppGuide: React.FC<{ dark?: boolean }> = ({ dark }) => {
  const [hidden, setHidden] = useState(() =>
    isNativeShell() || isStandalone() || !isPersonalDevice() || dismissedRecently());
  const [canPrompt, setCanPrompt] = useState(canPromptInstall());
  useEffect(() => onInstallPromptChange(() => setCanPrompt(canPromptInstall())), []);

  if (hidden) return null;

  const ios = isIOS();
  const ua = navigator.userAgent || '';
  // LINE などのアプリ内ブラウザや、Safari 以外では「ホーム画面に追加」が出ないことがある
  const iosNotSafari = ios && /CriOS|FxiOS|EdgiOS|Line\/|FBAN|FBAV|Instagram/.test(ua);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setHidden(true);
  };
  const install = async () => {
    const ok = await promptInstall();
    if (ok) setHidden(true);
  };

  const box: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', borderRadius: 14, padding: '12px 14px', margin: '10px 0 4px',
    background: dark ? 'rgba(255,255,255,0.12)' : '#eff6ff',
    border: dark ? '1px solid rgba(255,255,255,0.25)' : '1px solid #bfdbfe',
    color: dark ? '#fff' : '#1e3a8a', fontSize: 12.5, lineHeight: 1.7, textAlign: 'left',
  };

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ fontSize: 13.5 }}>📲 ホーム画面に追加して使えます</strong>
        <button onClick={dismiss} aria-label="閉じる"
          style={{ background: 'none', border: 'none', color: 'inherit', opacity: 0.7, fontSize: 16, cursor: 'pointer', padding: 2 }}>✕</button>
      </div>
      {ios ? (
        iosNotSafari ? (
          <div>この画面を<strong>Safari</strong>で開き直してから、共有ボタン <ShareIcon /> →「<strong>ホーム画面に追加</strong>」→「追加」を押してください。</div>
        ) : (
          <div>画面下の共有ボタン <ShareIcon /> を押し、「<strong>ホーム画面に追加</strong>」→ 右上の「<strong>追加</strong>」。次からはホーム画面の「カツオ勤怠」アイコンで開けます。</div>
        )
      ) : canPrompt ? (
        <>
          <div style={{ marginBottom: 8 }}>ボタンを押すと、ホーム画面にアプリとして追加されます。</div>
          <button onClick={install}
            style={{ width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            ホーム画面に追加する
          </button>
        </>
      ) : (
        <div>ブラウザのメニュー（右上の︙）から「<strong>ホーム画面に追加</strong>」または「<strong>アプリをインストール</strong>」を押してください。</div>
      )}
    </div>
  );
};
